import { memo } from 'preact/compat'
import { File, Download, Maximize2, Minimize2 } from 'lucide-preact'

interface AssetDocumentViewerProps {
  blobUrl: string | null
  fileName: string | null
  mimeType?: string | null
  expanded?: boolean
  onToggleExpand?: () => void
}

function getContentType(mimeType?: string | null, fileName?: string | null): 'image' | 'pdf' | 'video' | 'other' {
  if (mimeType) {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType === 'application/pdf') return 'pdf'
    if (mimeType.startsWith('video/')) return 'video'
  }

  if (fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase()
    if (ext && ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext)) return 'image'
    if (ext === 'pdf') return 'pdf'
    if (ext && ['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'].includes(ext)) return 'video'
  }

  return 'other'
}

function ExpandToggle({ expanded, onToggle }: { expanded?: boolean; onToggle: () => void }) {
  const Icon = expanded ? Minimize2 : Maximize2
  return (
    <button
      onClick={onToggle}
      className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
      title={expanded ? 'Show side panel' : 'Expand viewer'}
    >
      <Icon className="w-4 h-4" />
    </button>
  )
}

export const AssetDocumentViewer = memo(function AssetDocumentViewer({ blobUrl, fileName, mimeType, expanded, onToggleExpand }: AssetDocumentViewerProps) {
  if (!blobUrl) {
    return (
      <div className="relative flex flex-col items-center justify-center w-full h-full bg-slate-50 dark:bg-slate-950 text-slate-400 dark:text-slate-500">
        {onToggleExpand && <ExpandToggle expanded={expanded} onToggle={onToggleExpand} />}
        <File className="w-16 h-16 mb-3" />
        <p className="text-sm">No file available for preview</p>
      </div>
    )
  }

  const contentType = getContentType(mimeType, fileName)

  return (
    <div className="relative flex items-center justify-center w-full h-full bg-slate-50 dark:bg-slate-950 p-4 overflow-auto">
      {onToggleExpand && <ExpandToggle expanded={expanded} onToggle={onToggleExpand} />}
      {contentType === 'image' && (
        <img
          src={blobUrl}
          alt={fileName || 'Asset preview'}
          className="max-w-full max-h-full object-contain rounded"
        />
      )}

      {contentType === 'pdf' && (
        <iframe
          src={blobUrl}
          title={fileName || 'PDF preview'}
          className="w-full h-full border-0 rounded"
        />
      )}

      {contentType === 'video' && (
        <video
          src={blobUrl}
          controls
          className="max-w-full max-h-full rounded"
        >
          Your browser does not support video playback.
        </video>
      )}

      {contentType === 'other' && (
        <div className="flex flex-col items-center gap-4 text-slate-500 dark:text-slate-400">
          <File className="w-16 h-16" />
          {fileName && <p className="text-sm font-medium">{fileName}</p>}
          <a
            href={blobUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download File
          </a>
        </div>
      )}
    </div>
  )
})
